import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import { useReportsQueriesSelector } from "store";
import {
  CanvasView,
  CardData,
  DEFAULT_VIEW_ITEMS,
  DragPreview,
  GRID_CONSTANTS,
  PageMeta,
  ResizingCard,
  ViewDefinition
} from "store/types";

interface ReportGeneratorContextValue {
  // State
  sessionId: string | null;
  availableViews: ViewDefinition[];
  pages: CanvasView[][];
  currentPageIndex: number;
  selectedView: string | null;
  loading: boolean;
  error: string | null;
  reportTitle: string;
  logoUrl: string | null;
  logoFile: File | null;
  backgroundUrl: string | null;
  backgroundFile: File | null;
  pageMeta: PageMeta;
  draggedView: ViewDefinition | null;
  draggedCanvasView: CanvasView | null;
  dragPreview: DragPreview | null;
  resizingCard: ResizingCard | null;
  customizingView: CanvasView | null;

  // Setters
  setSessionId: (id: string | null) => void;
  setAvailableViews: (views: ViewDefinition[]) => void;
  setPages: React.Dispatch<React.SetStateAction<CanvasView[][]>>;
  setCurrentPageIndex: (index: number) => void;
  setSelectedView: (viewId: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setReportTitle: (title: string) => void;
  setLogoUrl: (url: string | null) => void;
  setLogoFile: (file: File | null) => void;
  setBackgroundUrl: (url: string | null) => void;
  setBackgroundFile: (file: File | null) => void;
  setPageMeta: React.Dispatch<React.SetStateAction<PageMeta>>;
  setDraggedView: (view: ViewDefinition | null) => void;
  setDraggedCanvasView: (view: CanvasView | null) => void;
  setDragPreview: (preview: DragPreview | null) => void;
  setResizingCard: (card: ResizingCard | null) => void;
  setCustomizingView: (view: CanvasView | null) => void;

  // Computed
  currentPageCards: CanvasView[];
  gridCellWidth: number;
  maxRow: number;

  // Actions
  addPage: () => void;
  removePage: (index: number) => void;
  addCardToCanvas: (view: ViewDefinition, col: number, row: number, span: number) => void;
  removeCardFromCanvas: (cardId: string) => void;
  updateCardPosition: (cardId: string, col: number, row: number) => void;
  updateCardSpan: (cardId: string, span: number, rowSpan?: number) => void;
  updateCardData: (cardId: string, data: CardData | undefined) => void;
  updateCardConfig: (cardId: string, config: CanvasView["customConfig"]) => void;
  clearCanvas: () => void;
  snapToGrid: (x: number, y: number) => { col: number; row: number };
  getCardWidth: (span: number) => number;
  getCardHeight: (rowSpan: number) => number;
  isPositionOccupied: (col: number, row: number, span: number, excludeId?: string) => boolean;
  findAvailablePosition: (span: number, rowSpan?: number) => { col: number; row: number } | null;
}

const ReportGeneratorContext = createContext<ReportGeneratorContextValue | null>(null);

export const useReportGeneratorContext = (): ReportGeneratorContextValue => {
  const context = useContext(ReportGeneratorContext);
  if (!context) {
    throw new Error("useReportGeneratorContext must be used within a ReportGeneratorProvider");
  }
  return context;
};

interface ReportGeneratorProviderProps {
  children: React.ReactNode;
}

export const ReportGeneratorProvider: React.FC<ReportGeneratorProviderProps> = ({ children }) => {
  // Core state
  const { currency, dateRange } = useReportsQueriesSelector();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [availableViews, setAvailableViews] = useState<ViewDefinition[]>(DEFAULT_VIEW_ITEMS);
  const [pages, setPages] = useState<CanvasView[][]>([[]]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportTitle, setReportTitle] = useState("Altius Report");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [pageMeta, setPageMeta] = useState<PageMeta>({});

  // Drag state
  const [draggedView, setDraggedView] = useState<ViewDefinition | null>(null);
  const [draggedCanvasView, setDraggedCanvasView] = useState<CanvasView | null>(null);
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const [resizingCard, setResizingCard] = useState<ResizingCard | null>(null);
  const [customizingView, setCustomizingView] = useState<CanvasView | null>(null);

  // Computed values
  const { CANVAS_WIDTH, GRID_COLUMNS, GAP, GRID_CELL_HEIGHT, MARGIN, HEADER_HEIGHT, FOOTER_HEIGHT, CANVAS_HEIGHT } =
    GRID_CONSTANTS;

  const gridCellWidth = useMemo(
    () => (CANVAS_WIDTH - MARGIN * 2 - (GRID_COLUMNS - 1) * GAP) / GRID_COLUMNS,
    [CANVAS_WIDTH, MARGIN, GRID_COLUMNS, GAP]
  );

  const maxRow = useMemo(
    () => Math.floor((CANVAS_HEIGHT - MARGIN * 2 - HEADER_HEIGHT - FOOTER_HEIGHT) / (GRID_CELL_HEIGHT + GAP)),
    [CANVAS_HEIGHT, MARGIN, HEADER_HEIGHT, FOOTER_HEIGHT, GRID_CELL_HEIGHT, GAP]
  );

  const currentPageCards = useMemo(() => pages[currentPageIndex] || [], [pages, currentPageIndex]);

  // Grid utilities
  const snapToGrid = useCallback(
    (x: number, y: number) => {
      const adjustedX = x - MARGIN;
      const adjustedY = y - MARGIN - HEADER_HEIGHT;
      const col = Math.max(0, Math.min(Math.round(adjustedX / (gridCellWidth + GAP)), GRID_COLUMNS - 1));
      const row = Math.max(0, Math.min(Math.round(adjustedY / (GRID_CELL_HEIGHT + GAP)), maxRow));
      return { col, row };
    },
    [MARGIN, HEADER_HEIGHT, gridCellWidth, GAP, GRID_COLUMNS, GRID_CELL_HEIGHT, maxRow]
  );

  const getCardWidth = useCallback(
    (span: number) => {
      return span * gridCellWidth + (span - 1) * GAP;
    },
    [gridCellWidth, GAP]
  );

  const getCardHeight = useCallback(
    (rowSpan: number) => {
      return rowSpan * GRID_CELL_HEIGHT + (rowSpan - 1) * GAP;
    },
    [GRID_CELL_HEIGHT, GAP]
  );

  const isPositionOccupied = useCallback(
    (col: number, row: number, span: number, excludeId?: string) => {
      if (col < 0 || col + span > GRID_COLUMNS) return true;

      return currentPageCards.some((card) => {
        if (excludeId && card.id === excludeId) return false;

        const cardRowSpan = card.gridRowSpan || 1;
        const newRowEnd = row + 1;
        const cardRowEnd = card.gridRow + cardRowSpan;

        if (newRowEnd <= card.gridRow || row >= cardRowEnd) return false;

        const cardStart = card.gridCol;
        const cardEnd = card.gridCol + card.gridSpan;
        const newStart = col;
        const newEnd = col + span;

        if (newEnd <= cardStart || newStart >= cardEnd) return false;

        return true;
      });
    },
    [currentPageCards, GRID_COLUMNS]
  );

  const findAvailablePosition = useCallback(
    (span: number, rowSpan: number = 1): { col: number; row: number } | null => {
      for (let row = 0; row <= maxRow - rowSpan + 1; row++) {
        for (let col = 0; col <= GRID_COLUMNS - span; col += span) {
          let available = true;
          for (let r = 0; r < rowSpan && available; r++) {
            if (isPositionOccupied(col, row + r, span)) {
              available = false;
            }
          }
          if (available) {
            return { col, row };
          }
        }
      }
      return null;
    },
    [maxRow, GRID_COLUMNS, isPositionOccupied]
  );

  // Page actions
  const addPage = useCallback(() => {
    setPages((prev) => {
      setCurrentPageIndex(prev.length);
      return [...prev, []];
    });
  }, []);

  const removePage = useCallback(
    (index: number) => {
      if (pages.length <= 1) return;
      setPages((prev) => prev.filter((_, i) => i !== index));
      if (currentPageIndex >= index && currentPageIndex > 0) {
        setCurrentPageIndex(currentPageIndex - 1);
      }
    },
    [pages.length, currentPageIndex]
  );

  // Card actions
  const addCardToCanvas = useCallback(
    (view: ViewDefinition, col: number, row: number, span: number) => {
      const newCard: CanvasView = {
        id: `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: view.type,
        title: view.title,
        gridCol: col,
        gridRow: row,
        gridSpan: span,
        gridRowSpan: view.type === "data_table" ? 4 : 2,
        data: view.previewData,
        pageIndex: currentPageIndex,
        customConfig: {}
      };

      setPages((prev) => {
        const updated = [...prev];
        updated[currentPageIndex] = [...(updated[currentPageIndex] || []), newCard];
        return updated;
      });
    },
    [currentPageIndex]
  );

  const removeCardFromCanvas = useCallback(
    (cardId: string) => {
      setPages((prev) => {
        const updated = [...prev];
        updated[currentPageIndex] = updated[currentPageIndex].filter((card) => card.id !== cardId);
        return updated;
      });
    },
    [currentPageIndex]
  );

  const updateCardPosition = useCallback(
    (cardId: string, col: number, row: number) => {
      setPages((prev) => {
        const updated = [...prev];
        updated[currentPageIndex] = updated[currentPageIndex].map((card) =>
          card.id === cardId ? { ...card, gridCol: col, gridRow: row } : card
        );
        return updated;
      });
    },
    [currentPageIndex]
  );

  const updateCardSpan = useCallback(
    (cardId: string, span: number, rowSpan?: number) => {
      setPages((prev) => {
        const updated = [...prev];
        updated[currentPageIndex] = updated[currentPageIndex].map((card) =>
          card.id === cardId
            ? { ...card, gridSpan: span, ...(rowSpan !== undefined && { gridRowSpan: rowSpan }) }
            : card
        );
        return updated;
      });
    },
    [currentPageIndex]
  );

  const updateCardData = useCallback(
    (cardId: string, data: CardData | undefined) => {
      setPages((prev) => {
        const updated = [...prev];
        updated[currentPageIndex] = updated[currentPageIndex].map((card) =>
          card.id === cardId ? { ...card, data } : card
        );
        return updated;
      });
    },
    [currentPageIndex]
  );

  const updateCardConfig = useCallback(
    (cardId: string, config: CanvasView["customConfig"]) => {
      setPages((prev) => {
        const updated = [...prev];
        updated[currentPageIndex] = updated[currentPageIndex].map((card) => {
          if (card.id !== cardId) return card;

          // Extract chartData from config to update card.data
          const { chartData, ...restConfig } = config || {};
          return {
            ...card,
            customConfig: { ...card.customConfig, ...restConfig },
            ...(chartData !== undefined && { data: chartData })
          };
        });
        return updated;
      });
    },
    [currentPageIndex]
  );

  const clearCanvas = useCallback(() => {
    setPages([[]]);
    setPageMeta({});
    setCurrentPageIndex(0);
  }, []);

  const value: ReportGeneratorContextValue = useMemo(
    () => ({
      sessionId,
      availableViews,
      pages,
      currentPageIndex,
      selectedView,
      loading,
      error,
      reportTitle,
      logoUrl,
      logoFile,
      backgroundUrl,
      backgroundFile,
      pageMeta,
      draggedView,
      draggedCanvasView,
      dragPreview,
      resizingCard,
      customizingView,
      setSessionId,
      setAvailableViews,
      setPages,
      setCurrentPageIndex,
      setSelectedView,
      setLoading,
      setError,
      setReportTitle,
      setLogoUrl,
      setLogoFile,
      setBackgroundUrl,
      setBackgroundFile,
      setPageMeta,
      setDraggedView,
      setDraggedCanvasView,
      setDragPreview,
      setResizingCard,
      setCustomizingView,
      currentPageCards,
      gridCellWidth,
      maxRow,
      addPage,
      removePage,
      addCardToCanvas,
      removeCardFromCanvas,
      updateCardPosition,
      updateCardSpan,
      updateCardData,
      updateCardConfig,
      clearCanvas,
      snapToGrid,
      getCardWidth,
      getCardHeight,
      isPositionOccupied,
      findAvailablePosition
    }),
    [
      sessionId,
      availableViews,
      pages,
      currentPageIndex,
      selectedView,
      loading,
      error,
      reportTitle,
      logoUrl,
      logoFile,
      backgroundUrl,
      backgroundFile,
      pageMeta,
      dateRange,
      currency,
      draggedView,
      draggedCanvasView,
      dragPreview,
      resizingCard,
      customizingView,
      currentPageCards,
      gridCellWidth,
      maxRow,
      addPage,
      removePage,
      addCardToCanvas,
      removeCardFromCanvas,
      updateCardPosition,
      updateCardSpan,
      updateCardData,
      updateCardConfig,
      clearCanvas,
      snapToGrid,
      getCardWidth,
      getCardHeight,
      isPositionOccupied,
      findAvailablePosition
    ]
  );

  return <ReportGeneratorContext.Provider value={value}>{children}</ReportGeneratorContext.Provider>;
};
